import { useState } from 'react';
import { CoreBox, CoreTypography } from '@youscience/core';
import { Grid } from '@mui/material';

import HeadSection from '@components/HeadSection';
import FeatureCard from '../FeatureCard';
import PromotionalAlert from '../PromotionalAlert';
import { LearnMoreModal } from '../LearnMoreModal';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { ROUTES } from '@constants/routes';
import { useAuthStoreContext } from '@context/authStore';
import { ECP_HASH } from '@constants';
import { EDUCATION_CONNECTIONS_URL } from '@constants/externalRoutes';

const GraduateCapIcon = () => (
  <svg width='56' height='56' viewBox='0 0 80 80' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M4.56293 41.604H23.9452L20.1965 58.2123C20.1718 58.2873 20.1718 58.3874 20.1718 58.4874C20.1718 64.5154 30.4539 66.6664 40.095 66.6664C49.736 66.6664 60.0182 64.5154 60.0182 58.4874C60.0182 58.3874 59.9935 58.2123 59.9935 58.2123L56.2447 41.604H75.4283C75.8475 41.604 76.242 41.3789 76.4639 41.0288C76.6858 40.6786 76.7351 40.2284 76.5625 39.8532L67.0201 17.4171C66.8229 16.9669 66.379 16.6667 65.8859 16.6667H16.3738C15.93 16.6667 15.5108 16.9169 15.2889 17.3171L3.47801 39.7781C3.28075 40.1533 3.28075 40.6286 3.50266 41.0037C3.72458 41.3789 4.1191 41.604 4.56293 41.604ZM6.6095 39.1028H25.3885C25.4614 39.0961 25.5357 39.096 25.6105 39.1028H54.542C54.6577 39.0853 54.7724 39.086 54.8834 39.1028H73.5543L65.0722 19.168H17.1135L13.2389 26.5215H39.0338C39.7242 26.5215 40.2667 27.0717 40.2667 27.7721C40.2667 28.4724 39.7242 29.0227 39.0338 29.0227H11.9209L6.6095 39.1028ZM22.6375 58.6125L26.4853 41.604H53.7046L57.5524 58.6125C57.3552 61.789 49.9333 64.1652 40.095 64.1652C30.2567 64.1652 22.8348 61.789 22.6375 58.6125Z'
      fill='currentColor'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.4316 45.6557V56.4261C11.9439 56.5735 13.1232 57.8373 13.1927 59.3882L14.031 63.2651C14.031 63.2651 14.0557 63.4402 14.0557 63.5402C14.0557 65.2661 12.6749 66.6667 10.9735 66.6667H7.37355C5.67219 66.6667 4.29137 65.2661 4.29137 63.5402C4.29137 63.4402 4.29137 63.3651 4.31603 63.2651L5.15438 59.3882C5.22427 57.8285 6.43873 56.559 7.9659 56.4236V45.6557C7.9659 44.9553 8.50837 44.405 9.19877 44.405C9.88918 44.405 10.4316 44.9553 10.4316 45.6557ZM9.19877 58.9372C9.11377 58.9372 9.031 58.9289 8.95122 58.9129H8.2119C7.8667 58.9129 7.59547 59.1881 7.59547 59.5382C7.59547 59.6383 7.59547 59.7133 7.57081 59.8134L6.73246 63.6403H6.78177C6.83109 63.9404 7.07766 64.1655 7.37355 64.1655H10.9982C11.2941 64.1655 11.5407 63.9404 11.59 63.6403L10.7516 59.8134C10.7516 59.8134 10.727 59.6383 10.727 59.5382C10.727 59.1881 10.4557 58.9129 10.1105 58.9129H9.44633C9.36654 58.9289 9.28378 58.9372 9.19877 58.9372Z'
      fill='currentColor'
    />
  </svg>
);

const CommonAppIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='56' height='57' viewBox='0 0 56 57' fill='none'>
    <path
      d='M47.2661 0.725948C44.1585 2.17586 16.9492 14.9874 14.6718 16.1046C10.4255 18.2438 8.17193 21.4288 8.17193 25.3745C8.19566 28.7735 10.046 31.0078 10.9237 31.8872C10.0697 32.7429 8.2431 34.8821 8.17193 37.7107C8.05332 41.894 9.87994 45.1266 13.4857 47.0282L33.3886 56.4883C33.5073 56.5358 33.6496 56.5833 33.7682 56.5833C33.9343 56.5833 34.1003 56.5358 34.2427 56.4407C34.5036 56.2743 34.6459 55.9891 34.6459 55.6801V44.366C34.6459 41.7514 33.1277 39.3507 30.8029 38.2336L17.5897 31.8397L44.5144 19.0995C46.9578 17.9348 48.5471 15.439 48.5471 12.7056V1.72425C48.5471 1.5341 48.5471 0.963641 48.1439 0.702181C48.0015 0.607105 47.8829 0.583336 47.7643 0.583336C47.5271 0.583336 47.361 0.67841 47.2661 0.725948ZM13.5569 29.4153C13.5332 29.3915 11.7303 27.894 11.7303 25.327C11.7303 23.9721 12.1335 22.8074 12.9638 21.7854C13.7229 20.8584 14.8142 20.0265 16.2138 19.3134C18.0878 18.4102 36.3776 9.78198 44.9651 5.765V12.7056C44.9651 14.0366 44.1823 15.2726 42.9961 15.8431L13.8415 29.6292L13.5569 29.4153ZM15.0751 43.7718C12.6792 42.4883 11.6354 40.6343 11.7303 37.782C11.7777 35.8567 13.5569 34.2879 13.628 34.2404L13.8653 34.0502L29.2847 41.4424C30.3759 41.9653 31.0876 43.1063 31.0876 44.3423V51.4017L15.0751 43.7718Z'
      fill='url(#paint0_linear_17360_21826)'
    />
    <defs>
      <linearGradient
        id='paint0_linear_17360_21826'
        x1='28.3428'
        y1='56.5351'
        x2='28.3428'
        y2='4.90327'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#429AE2' />
        <stop offset='0.5129' stopColor='#38DAF9' />
        <stop offset='1' stopColor='#9AE9FF' />
      </linearGradient>
    </defs>
  </svg>
);

const ResumeBuilderIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 51.3333 51.3333' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M2.72789 51.3333H37.755C39.2647 51.3333 40.5009 50.1863 40.5009 48.765V45.3747C40.5009 44.8952 40.0943 44.5186 39.5796 44.5186C39.3331 44.5186 39.1118 44.6043 38.9481 44.746C38.8509 44.8304 38.7735 44.935 38.7231 45.0528C38.6817 45.1515 38.6583 45.2602 38.6583 45.3747V48.765C38.6583 48.8674 38.6386 48.9664 38.6026 49.0588C38.5828 49.109 38.5576 49.1575 38.5288 49.203L38.4658 49.2875C38.3992 49.369 38.3165 49.4392 38.2229 49.494C38.0861 49.5743 37.926 49.6211 37.755 49.6211H2.72789C2.24745 49.6211 1.84259 49.2444 1.84259 48.765V2.58552C1.84259 2.10604 2.24745 1.71225 2.72789 1.71225H29.4454V9.4174C29.4454 9.89688 29.8503 10.2735 30.3667 10.2735H38.6583V14.5713C38.6583 15.0508 39.0632 15.4274 39.5796 15.4274C40.0943 15.4274 40.5009 15.0508 40.5009 14.5713V9.39482C40.4937 9.00062 40.3804 8.65324 40.2202 8.38152C40.0691 8.12401 39.8747 7.92043 39.6894 7.75656L32.0617 0.753291C31.6929 0.410924 31.2322 0 30.3667 0H2.72789C1.23439 0 0 1.16422 0 2.58552V48.765C0 49.4313 0.268111 50.0374 0.707165 50.4931C0.876309 50.6678 1.07064 50.8208 1.28477 50.9462C1.70223 51.1916 2.19707 51.3333 2.72789 51.3333ZM31.288 2.44548V8.56127H37.917L31.288 2.44548Z'
      fill='currentColor'
    />
    <path
      d='M21.2455 35.8888H8.29164C8.18188 35.8888 8.07751 35.8716 7.98035 35.8399C7.76622 35.7705 7.59168 35.6313 7.48551 35.4511C7.44952 35.3896 7.42073 35.3236 7.40094 35.2534C7.38115 35.1831 7.37035 35.1091 7.37035 35.0326C7.37035 34.5532 7.77521 34.1765 8.29164 34.1765H21.2455C21.7602 34.1765 22.1668 34.5532 22.1668 35.0326C22.1668 35.5121 21.7602 35.8888 21.2455 35.8888Z'
      fill='currentColor'
    />
    <path
      d='M8.29164 29.0226H21.2455C21.7602 29.0226 22.1668 28.646 22.1668 28.1665C22.1668 27.687 21.7602 27.3104 21.2455 27.3104H8.29164C7.77521 27.3104 7.37035 27.687 7.37035 28.1665C7.37035 28.646 7.77521 29.0226 8.29164 29.0226Z'
      fill='currentColor'
    />
    <path
      d='M21.2455 22.1736H8.29164C8.01633 22.1736 7.77342 22.0666 7.60607 21.8944C7.45852 21.743 7.37035 21.5415 7.37035 21.3175C7.37035 20.838 7.77521 20.4613 8.29164 20.4613H21.2455C21.7602 20.4613 22.1668 20.838 22.1668 21.3175C22.1668 21.5231 22.0913 21.7096 21.9653 21.8559C21.798 22.0507 21.5388 22.1736 21.2455 22.1736Z'
      fill='currentColor'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M27.583 42.6349H49.5843L49.5645 42.6182C50.5416 42.6182 51.3333 41.8645 51.3333 40.9227V37.9777C51.3333 37.2758 50.9105 36.6592 50.2285 36.4025C43.7974 33.9541 42.6188 32.9095 42.3975 32.6357V31.9676C43.1353 31.1458 43.7057 30.1016 44.0745 28.9202C44.3319 28.6811 44.5496 28.3939 44.7061 28.0762C44.8303 27.8254 44.9185 27.5557 44.9581 27.2761C45.0696 26.6256 44.9221 25.9748 44.5532 25.41V23.0297C44.5532 19.5709 42.3417 17.5849 38.5828 17.5849C34.8238 17.5849 32.5944 19.6224 32.5944 23.0297V25.41C32.2453 25.9576 32.0977 26.6085 32.1895 27.2761C32.2813 27.9098 32.5944 28.4921 33.0748 28.9202C33.4419 30.1016 34.0141 31.1458 34.75 31.9676V32.6357C34.5107 32.9437 33.2943 33.9884 26.919 36.4197C26.2569 36.6763 25.8142 37.2929 25.8142 37.9948V40.9398C25.8142 41.8817 26.606 42.6349 27.583 42.6349ZM34.4369 23.0297C34.4369 21.3204 35.1549 19.3042 38.5648 19.2971C41.2981 19.303 42.6746 20.5529 42.6746 23.0297V25.6667C42.6746 25.7524 42.6854 25.8343 42.7106 25.9129C42.7394 26.0074 42.788 26.0964 42.8581 26.1804C42.9697 26.3125 43.0435 26.4659 43.0795 26.6286C43.1083 26.7602 43.1137 26.8986 43.0975 27.0366C43.0615 27.3275 42.9139 27.5846 42.6746 27.7384C42.5073 27.8413 42.3975 28.0127 42.3417 28.2008C42.0106 29.3483 41.4762 30.3411 40.7762 31.0434C40.6287 31.1972 40.5369 31.4029 40.5369 31.6253V32.8066C40.5369 32.8438 40.5405 32.8777 40.5459 32.9116C40.5513 32.955 40.5621 32.9981 40.5729 33.0466C40.868 33.9541 42.1402 35.1698 49.5105 37.9777H49.4728L49.5465 40.9227H27.6388L27.601 37.9777C34.9714 35.1698 36.2615 33.9541 36.5387 33.0466C36.5746 32.978 36.5746 32.8066 36.5746 32.8066V31.6253C36.5746 31.4029 36.4829 31.1972 36.3353 31.0434C35.6354 30.3411 35.1009 29.365 34.7698 28.2008C34.7518 28.1084 34.7105 28.0244 34.6547 27.9483C34.5971 27.8693 34.5215 27.7995 34.4369 27.7384C34.2156 27.5846 34.0501 27.3275 34.0141 27.0366C33.9763 26.7285 34.0501 26.42 34.2534 26.1804C34.3632 26.0433 34.4369 25.8552 34.4369 25.6667V23.0297Z'
      fill='currentColor'
    />
  </svg>
);

export const NonLearnerLandingCCRDisabled = () => {
  const { currentAccess } = useAuthStoreContext();
  const isCommonAppEnabled = currentAccess.tenant?.productAvailability?.commonApp;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLearnMore = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <CoreBox>
      <HeadSection
        title='Education and Career planning'
        subtitle='Discover student resources supporting self-reflection, career exploration, resume building, course planning, and postsecondary education options.'
        breadcrumbsCurrentPage={BREADCRUMBS_PAGE_TEXT.LANDING}
      />

      <CoreBox sx={{ maxWidth: 1200, margin: '0 auto', padding: 4 }}>
        <CoreBox sx={{ marginBottom: 4 }}>
          <PromotionalAlert onLearnMore={handleLearnMore} />
        </CoreBox>

        <CoreBox sx={{ background: '#F7F7F7', padding: 4, borderRadius: 2 }}>
          <CoreBox sx={{ marginBottom: 6 }}>
            <CoreTypography variant='h3' sx={{ marginBottom: 3 }}>
              Explore planning tools
            </CoreTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <FeatureCard
                  title='Education and Career Plan'
                  description='Preview student resources for self-reflection, academic planning, and career exploration.'
                  icon={<GraduateCapIcon />}
                  path={ROUTES.LANDING}
                />
              </Grid>

              {isCommonAppEnabled && (
                <Grid item xs={12} sm={6} md={4}>
                  <FeatureCard
                    title='Common App'
                    description="Manage and prepare school and recommendation forms for your students' college applications"
                    icon={<CommonAppIcon />}
                    externalUrl={`${EDUCATION_CONNECTIONS_URL}/recommender-workspace${ECP_HASH}`}
                    useIconColor={true}
                  />
                </Grid>
              )}
            </Grid>
          </CoreBox>

          <CoreBox>
            <CoreTypography variant='h3' sx={{ marginBottom: 3 }}>
              Additional tools
            </CoreTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <FeatureCard
                  title='Resume builder'
                  description='Create a professional resume that highlights your talents, experiences, and achievements.'
                  icon={<ResumeBuilderIcon />}
                  path='/resume-builder'
                />
              </Grid>
            </Grid>
          </CoreBox>
        </CoreBox>
      </CoreBox>

      <LearnMoreModal open={isModalOpen} onClose={handleCloseModal} />
    </CoreBox>
  );
};
